import { useNow } from '@/shared/hooks/useNow';
import { formatFreshness } from '@/shared/utils/time';

/**
 * Whether the page is current, and how current (spec 113 US5; spec 122
 * FR-016; spec 126 FR-007, FR-008).
 *
 * Shared: the Overview, the board and the Tasks page all read on the same
 * schedule and all say so in the same place — right of the title, in
 * `PageHeader`'s `aside`.
 *
 * Three states, each a fact the query cache already holds:
 * - **Live** while a run is in flight and the page re-reads every two seconds;
 * - **Updated 20 s ago** otherwise — it used to say "Up to date", a claim an
 *   idle page that never re-read could not back;
 * - **Couldn't refresh · Retry** when the last re-read failed; what is on
 *   screen stays.
 *
 * Only the state word is in the live region, so a screen reader hears a change
 * of state once and not every tick of the age beside it. The age has its own
 * ten-second clock, so the page around it does not re-render on the tick. The
 * pulse stops when reduced motion is asked for; the word carries the meaning.
 */
export function LiveStatus({
  live,
  updatedAt,
  failed = false,
  retrying = false,
  onRetry,
}: {
  live: boolean;
  /** When the data on screen was read (`dataUpdatedAt`); 0 when never. */
  updatedAt: number;
  failed?: boolean;
  retrying?: boolean;
  onRetry?: () => void;
}) {
  const state = failed ? 'failed' : live ? 'live' : 'idle';
  return (
    <span
      role="group"
      aria-label="Refresh status"
      className="border-border bg-card text-muted-foreground inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-2xs"
    >
      <span
        aria-hidden="true"
        className={`size-1.5 rounded-full ${
          state === 'live'
            ? 'bg-board-state-done motion-safe:animate-pulse'
            : state === 'failed'
              ? 'bg-board-state-failed'
              : 'bg-muted-foreground/40'
        }`}
      />
      <span role="status">
        {state === 'live'
          ? 'Live'
          : state === 'failed'
            ? 'Couldn’t refresh'
            : 'Updated'}
      </span>
      {state === 'idle' && updatedAt > 0 ? <Age updatedAt={updatedAt} /> : null}
      {state === 'failed' && onRetry ? (
        <button
          type="button"
          onClick={onRetry}
          disabled={retrying}
          className="text-foreground underline-offset-2 hover:underline focus-visible:underline disabled:opacity-50"
        >
          {retrying ? 'Retrying…' : 'Retry'}
        </button>
      ) : null}
    </span>
  );
}

function Age({ updatedAt }: { updatedAt: number }) {
  const now = useNow(10_000);
  return <span>{formatFreshness(updatedAt, Math.max(now, updatedAt))}</span>;
}
